import { useState } from "react";
import { toast } from "sonner";
import { useSalon } from "../context/SalonContext";
import { adminLogin as apiAdminLogin, auth } from "../lib/api";

/**
 * AdminLogin — Super-admin login for the platform owner (Kinng.app).
 * Backend é a ÚNICA autoridade — POST /api/auth/admin/login.
 * Nenhum fallback local (removido para evitar bypass em versões antigas).
 */
const AdminLogin = () => {
    const { superAdminLogin } = useSalon();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [showPwd, setShowPwd] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await apiAdminLogin(email.trim(), password);
            // Backend gravou kinng.admin_token — sinaliza sessão no SalonContext.
            superAdminLogin();
            toast.success("Acesso liberado", {
                description: "Bem-vindo ao painel mestre da plataforma.",
            });
        } catch (err) {
            // Falha no login → limpa qualquer resquício e mostra erro. Nunca destrava a sessão.
            auth.clearAdmin();
            const status = err?.status;
            const description = status && status >= 500
                ? "Servidor indisponível. Tente em alguns instantes."
                : err?.message || "Verifique o e-mail e senha do super-admin.";
            toast.error("Credenciais inválidas", { description });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            data-testid="admin-login-screen"
            className="min-h-screen w-full bg-slate-950 text-white flex items-center justify-center px-4 relative overflow-hidden"
        >
            {/* Ambient */}
            <div
                className="absolute inset-0 opacity-60 pointer-events-none"
                style={{
                    background:
                        "radial-gradient(at 70% 20%, rgba(99,102,241,0.18) 0px, transparent 50%), radial-gradient(at 30% 80%, rgba(217,119,6,0.15) 0px, transparent 50%)",
                }}
            ></div>

            {/* Header */}
            <header className="absolute top-6 left-6 right-6 flex items-center justify-between z-10">
                <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg">
                        <i className="fa-solid fa-shield-halved text-white"></i>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">
                            Painel Mestre
                        </div>
                        <div className="salon-heading text-lg">Kinng.app Admin</div>
                    </div>
                </div>
                <a
                    href="/"
                    data-testid="admin-back-home"
                    className="text-xs text-slate-400 hover:text-white transition-colors"
                >
                    <i className="fa-solid fa-arrow-left mr-1.5"></i>Voltar para o site
                </a>
            </header>

            <form
                onSubmit={handleSubmit}
                data-testid="admin-login-form"
                className="relative z-10 w-full max-w-md rounded-3xl border border-white/10 bg-slate-900/80 backdrop-blur-xl p-8 md:p-10 shadow-2xl"
            >
                <div
                    className="absolute -inset-px rounded-3xl opacity-30 blur-2xl pointer-events-none"
                    style={{ background: "linear-gradient(135deg, #6366f1, #4f46e5)" }}
                ></div>

                <div className="relative">
                    <div className="flex items-center justify-between mb-8">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.3em] text-indigo-400">
                                Acesso Restrito
                            </div>
                            <h2 className="salon-heading text-2xl mt-1">Painel da Plataforma</h2>
                        </div>
                        <div className="h-12 w-12 rounded-xl bg-indigo-500/15 flex items-center justify-center">
                            <i className="fa-solid fa-key text-indigo-400 text-lg"></i>
                        </div>
                    </div>

                    <label className="block">
                        <span className="text-[11px] uppercase tracking-[0.25em] text-slate-400">
                            E-mail do super-admin
                        </span>
                        <input
                            data-testid="admin-email-input"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="admin@kinng.app"
                            className="mt-2 w-full rounded-xl border border-white/10 bg-slate-950/60 px-4 py-3 text-white outline-none focus:border-indigo-500/60 transition-colors min-h-[48px]"
                            autoComplete="email"
                        />
                    </label>

                    <label className="block mt-4">
                        <span className="text-[11px] uppercase tracking-[0.25em] text-slate-400">
                            Senha
                        </span>
                        <div className="mt-2 flex items-center rounded-xl border border-white/10 bg-slate-950/60 focus-within:border-indigo-500/60 transition-colors">
                            <input
                                data-testid="admin-password-input"
                                type={showPwd ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                className="flex-1 bg-transparent px-4 py-3 text-white outline-none min-h-[48px]"
                                autoComplete="current-password"
                            />
                            <button
                                type="button"
                                data-testid="admin-toggle-pwd"
                                onClick={() => setShowPwd((s) => !s)}
                                className="px-3 text-slate-400 hover:text-white text-sm min-h-[44px] min-w-[44px]"
                            >
                                <i className={`fa-solid ${showPwd ? "fa-eye-slash" : "fa-eye"}`}></i>
                            </button>
                        </div>
                    </label>

                    <button
                        type="submit"
                        data-testid="admin-login-submit"
                        disabled={loading}
                        className="mt-7 w-full min-h-[52px] rounded-xl bg-indigo-500 hover:bg-indigo-400 active:bg-indigo-600 text-white font-semibold uppercase tracking-[0.2em] text-sm transition-all duration-300 shadow-lg shadow-indigo-500/20 disabled:opacity-60"
                    >
                        {loading ? (
                            <>
                                <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                                Verificando credenciais…
                            </>
                        ) : (
                            <>
                                Acessar Painel Mestre <i className="fa-solid fa-arrow-right ml-2"></i>
                            </>
                        )}
                    </button>

                    <div className="mt-6 flex items-center justify-between text-xs text-slate-500">
                        <span>
                            <i className="fa-solid fa-lock mr-1.5 text-indigo-400/70"></i>
                            Conexão segura
                        </span>
                        <span className="text-indigo-400/80">SSO 2FA disponível</span>
                    </div>
                </div>
            </form>

            <footer className="absolute bottom-6 left-0 right-0 text-center text-xs text-slate-600 z-10">
                © {new Date().getFullYear()} Kinng.app Platform · Painel Mestre v1.0
            </footer>
        </div>
    );
};

export default AdminLogin;
